import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../ui/Button.js";

export const RING_PORTAL_URL = "https://www.intelelead.com/portal/";

const REDIRECT_SECONDS = 3;

/**
 * The end of setup. When the agent has just finished the last step we
 * congratulate them and hand them straight to the Ring portal, counting down so
 * the jump isn't a surprise. Coming back to a setup that was already complete
 * shows the same card without the countdown — bouncing them out of their own
 * portal on every visit would trap them away from their forms and account.
 */
export function SetupCompleteCard({ redirect }: { redirect: boolean }) {
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (!redirect) return;
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [redirect]);

  useEffect(() => {
    if (!redirect || secondsLeft > 0) return;
    window.location.assign(RING_PORTAL_URL);
  }, [redirect, secondsLeft]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-card border-2 border-status-active-border bg-status-active-bg px-6 py-10 text-center shadow-card sm:px-10 sm:py-14"
      aria-labelledby="setup-complete-title"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-status-active-text">
        Setup complete
      </p>
      <h2
        id="setup-complete-title"
        className="mt-3 font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.045em] text-content-primary sm:text-5xl"
      >
        Congratulations.
      </h2>

      {redirect ? (
        <>
          <p role="status" aria-live="polite" className="mt-4 text-base font-semibold text-content-secondary">
            Redirecting to your Ring portal…{" "}
            <span className="font-mono text-content-primary">
              {secondsLeft > 0 ? `in ${secondsLeft}…` : "now"}
            </span>
          </p>
          <p className="mt-6 text-sm text-content-secondary">
            Not moving?{" "}
            <a
              href={RING_PORTAL_URL}
              className="focus-ring rounded font-semibold text-content-primary underline underline-offset-2"
            >
              Open the Ring portal
            </a>
            .
          </p>
        </>
      ) : (
        <>
          <p className="mt-4 text-base font-semibold text-content-secondary">
            You're all set up and ready to take calls.
          </p>
          <Button
            className="mt-7"
            size="lg"
            onClick={() => window.location.assign(RING_PORTAL_URL)}
          >
            Open your Ring portal
            <span aria-hidden="true">→</span>
          </Button>
        </>
      )}
    </motion.section>
  );
}
